import React, { useState, useEffect } from 'react';
import { ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import ReminderEmailForm from './ReminderEmailForm';
import { usePreview } from '@dashboard/context/PreviewContext';
import usePage from '@dashboard/hooks/usePage';
import { Form } from '@dashboard/components/Formik';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import Link from '@/dashboard/components/Link';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';

// Events and forms have no product, so fall back to the page title as the reminder email does.
export const getDefaultSubjectLine = page =>
  `Complete your purchase for ${page?.product?.title || page?.productTitle || page?.title}`;

const CheckoutAbandonmentReminderEmailsForm = () => {
  const { seller } = useAuth();
  const { page, updatePage } = usePage();
  const [openReminder, setOpenReminder] = useState(null);
  const [, previewDispatch] = usePreview();
  const setPreview = value => previewDispatch({ type: 'set', value });

  const handleValueChange = value => {
    setOpenReminder(value ? Number(value) : null);
  };

  useEffect(() => {
    if (openReminder === null) {
      setPreview({
        type: 'reminderEmailMessage',
        reminderNumber: 1,
      });
    }
  }, [openReminder]);

  useEffect(() => {
    return () => {
      setPreview({
        type: 'checkout',
      });
    };
  }, []);

  const getSubjectLine = reminderNumber => {
    const reminderKey = `reminder${reminderNumber}`;
    const reminderData = page?.checkoutAbandonment?.emailReminders?.[reminderKey];

    if (reminderData?.customizeEmail === true) {
      return reminderData?.subject;
    }

    return getDefaultSubjectLine(page);
  };

  const reminders = [
    { id: 1, title: 'Reminder 1', subtitle: '1 hour after abandonment' },
    { id: 2, title: 'Reminder 2', subtitle: '24 hours after abandonment' },
    { id: 3, title: 'Reminder 3', subtitle: '48 hours after abandonment' },
  ];

  const initialValues = {
    disableEmails: page?.checkoutAbandonment?.disableEmails || false,
    showStoreLogo: page?.checkoutAbandonment?.showStoreLogo !== false,
    showStoreName: page?.checkoutAbandonment?.showStoreName !== false,
  };

  const sellerEmailsDisabled = !!seller && seller?.checkoutAbandonment?.sendEmails !== true;

  const callout = sellerEmailsDisabled
    ? {
        link: '/settings/cart-abandonment',
        text: 'store settings',
      }
    : null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {callout && (
          <Alert variant="warning" className="my-4">
            <ExclamationCircleIcon />
            <AlertTitle>Cart abandonment emails are turned off for this store</AlertTitle>
            <AlertDescription>
              <p>
                Enable in <Link href={callout.link}>{callout.text}</Link>.
              </p>
            </AlertDescription>
          </Alert>
        )}

        <Accordion
          type="single"
          collapsible
          value={openReminder ? String(openReminder) : ''}
          onValueChange={handleValueChange}
          className="border-t"
        >
          {reminders.map(reminder => (
            <AccordionItem
              key={reminder.id}
              value={String(reminder.id)}
              className="border-b last:border-b"
            >
              <AccordionTrigger className="hover:no-underline">
                <div className="flex flex-col items-start justify-start gap-y-1">
                  <div className="text-sm font-semibold break-words">{reminder.title}</div>
                  <div className="flex items-center gap-x-1 text-sm text-gray-700 dark:text-muted-foreground">
                    <ClockIcon className="w-3.5 h-3.5" />
                    {reminder.subtitle}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-muted-foreground">
                    Subject: {getSubjectLine(reminder.id)}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <ReminderEmailForm reminderNumber={reminder.id} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <Form
        autosave
        showState={false}
        initialValues={initialValues}
        onSubmit={async values => {
          const payload = {
            checkoutAbandonment: {
              ...page?.checkoutAbandonment,
              disableEmails: values.disableEmails,
              showStoreLogo: values.showStoreLogo,
              showStoreName: values.showStoreName,
            },
          };

          const { data, error } = await updatePage.mutateAsync(payload);

          return { data, error };
        }}
        fields={() => {
          return [
            {
              type: 'label',
              name: 'label',
              label: 'Show on emails',
              noMargin: true,
            },
            {
              type: 'checkbox',
              name: 'showStoreLogo',
              label: 'Logo',
            },
            {
              type: 'checkbox',
              name: 'showStoreName',
              label: 'Store name',
            },
            {
              type: 'divider',
            },
            {
              type: 'checkbox',
              name: 'disableEmails',
              label: 'Disable email reminders for this page',
            },
          ];
        }}
      ></Form>
    </div>
  );
};

export default CheckoutAbandonmentReminderEmailsForm;
